import {
  Button,
  Dialog,
  Divider,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Popper,
  Paper,
  MenuItem,
} from "@mui/material";
import { Box } from "@mui/system";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import UserInteractionsService from "../../services/interactions/Interactions";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GlobalContext } from "../../context/GlobalContext";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { getUserAttributes } from "../../utils/auth";
import { aiSupportedExtensions, getFileExtension, capitalizeFirstLetter, isCondensedTranscript, isGroupAiCompleted, secondsToHHMMSS, getGroupStatusMap } from "../../utils/utils";
import ProgressSpinner from "../Common/ProgressSpinner";
import JoinMeeting from "../Integrations/JoinMeeting";
import NotesService from "../../services/notes/NoteService";
import { NoteData } from "../../utils/types";
import CollateralService from "../../services/collaterals/CollateralService";
import Collaterals from "../MeetingDetails/Collaterals";
import Notes from "../MeetingDetails/Notes/Notes";
import SlidesPage from "./SlidesPage";
import TopicsPage from "./TopicsPage";
import CallStatsPage from "./CallStatsPage";
import Meeting from "../MeetingDetails/Meeting";
import AbstractiveSummaryPage from "./AbstractiveSummaryPage";
import ActionItemService from "../../services/actionItems/ActionItemService";
import "../../styles/common.css";
import useGlobalContext from "../../context/useGlobalContext";
import DefaultExtractiveSummyIcon from "../../assets/interaction-detail-page/Extractive Summary Default.svg";
import SelectedExtractiveSummyIcon from "../../assets/interaction-detail-page/Extractive Summary Active.svg";
import DefaultAbstractivSummyIcon from "../../assets/interaction-detail-page/Abstractive Summary Default.svg";
import SelectedAbstractivSummyIcon from "../../assets/interaction-detail-page/Abstractive Summary Selected.svg";
import DefaultCollateralsIcon from "../../assets/interaction-detail-page/Collaterals Default.svg";
import SelectedCollateralsIcon from "../../assets/interaction-detail-page/Collaterals Selected.svg";
import DefaultNotesIcon from "../../assets/interaction-detail-page/Notes Default.svg";
import SelectedNotesIcon from "../../assets/interaction-detail-page/Notes Selected.svg";
import SelectedAsk7GIcon from "../../assets/interaction-detail-page/selectedAsk7g_icon.svg";
import "../../styles/interactionDetailsPage.css";
import NotePage from "./NotePage";
import InteractionActionItems from "./InteractionActionItems";
import InteractionCallQuality from "./InteractionCallQuality";
import UnauthorizedAccess from "../Common/UnauthorizedAccess";
import CopySvg from "../../assets/common/copy.svg";
import {
  getTopicSummaryHTML,
  getSentimentHTML,
  getKeyTakeawaysHTML,
  getCallQualityHTML,
  getActionItemsHTML
} from './copyTextFormatter';
import ShareIcon from "../../assets/meeting/share-meeting.svg";
import ShareInteractionDialog from "./ShareInteractionDialog";
import InteractionAsk7G from "./InteractionAsk7G";
import ManualBotService from "../../services/manualBot/ManualBotJoinservice";
import { useQueryClient } from "@tanstack/react-query";
import ArrowBackIcon from "../../assets/interaction-detail-page/idp-arrow-back.svg";
import logo7G from "../../assets/sidebar/logo7G Secondary.svg";
import logo7GMagenta from "../../assets/common/logo7G.svg";
import EllipsedTooltip from "../Common/EllipsedTooltip";
import FrameworkAnalysis from "./FrameworkAnalysis";
import DeleteIcon from "../../assets/action-items/slideOver-delete.svg";
import CustomConfirmation from "../Common/CustomConfirmation";

interface TabHeaderProps {
  label: string;
  count: number;
  defaultIcon?: React.ReactNode;
  selectedIcon?: React.ReactNode;
  isSelected?: boolean;
  inlineCount?: boolean;
  isTextHighlighted?: boolean;
}

interface CallQualitySection {
  score: string;
  reason: string;
  whatWentWell: string[];
  areasOfOpportunity: string[];
}

interface CallQualityData {
  opening?: CallQualitySection;
  execution?: CallQualitySection;
  closing?: CallQualitySection;
}
const tooltipTitles: Record<string, string> = {
  "extract-topics": "Copy Topics",
  "abstract-insights": "Copy Summary",
  "abstract-callQuality": "Copy Call Scoring",
  "extract-actionItems": "Copy Action Items",
};
const TabHeader = ({
  label,
  count,
  defaultIcon,
  selectedIcon,
  isSelected = false,
  inlineCount = false,
  isTextHighlighted = false,
}: TabHeaderProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const hasIcons = defaultIcon && selectedIcon;

  const iconToRender =
    hasIcons && (isSelected || isHovered) ? selectedIcon : defaultIcon;

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Optional Icon */}
      {iconToRender && (
        <Box sx={{ display: "flex", alignItems: "center" }}>{iconToRender}</Box>
      )}

      {/* Label + Inline Count */}
      {inlineCount ? (
        <Box
          sx={{
            display: "inline-flex",
            width: "fit-content",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              color: isSelected ? "#000" : "rgba(0, 0, 0, 0.5)",
              fontWeight: 400,
              fontFamily: 'var(--font-family)',
              whiteSpace: "nowrap",
              fontSize: "13px",
            }}
          >
            {label}
          </Typography>
          {count > 0 && (
            <Typography
              sx={{
                fontSize: "13px",
                color: isSelected ? "#000" : "rgba(0, 0, 0, 0.5)",
                fontWeight: 400,
                fontFamily: 'var(--font-family)',
              }}
            >
              [{count}]
            </Typography>
          )}
        </Box>
      ) : (
        <>
          {/* Just Label */}
          <Typography
            variant="h2"
            sx={{
              color: (isSelected && isTextHighlighted) ? "rgb(127, 1, 139)" : "rgba(26, 26, 26, 0.8)",
              fontWeight: 500,
              fontFamily: 'var(--font-family)',
            }}
          >
            {label}
          </Typography>

          {/* Floating Badge */}
          {count > 0 && (
            <Box
              sx={{
                background: "rgba(50, 189, 0, 0.19)",
                color: "#32BD00",
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "absolute",
                top: "-12px",
                right: "-12px",
                visibility: "hidden",
              }}
            >
              <Typography variant="subtitle1">{count}</Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

interface SlideFrame {
  frame_number: number;
  start_timestamp: number;
  end_timestamp: number;
  s3_url: string;
}
const MeetingDetailsPage = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const userEmail = location.state?.userEmail;
  const profileOrgId = location.state?.profileOrgId;
  const appliedFilters = location.state?.appliedFilters;
  const customDateRange = location.state?.customDateRange;
  const userName = location.state?.userName;
  const userDetails = location.state?.userDetails;
  const selectedNps = location.state?.selectedNps || null;
  const activeStat = location.state?.activeStat;
  const startEndMeetingTime = location.state?.startEndMeetingTime || null;
  const tableFilters = location.state?.tableFilters;
  const startEndMeetingTimes = location.state?.startEndMeetingTime || null;
  const slideOverParent = location.state?.slideOverParent || null;
  const isSlideOverOpen = location.state?.isSlideOverOpen;
  const clientDomain = location.state?.clientDomain;
  const view = location.state?.view || null;
  const type = location.state?.type || null;
  const external = location.state?.external;
  const selectedTabs = location.state?.selectedTab ?? query.get("st") ?? "";
  const newUserTeamData = location.state?.newUserTeamData;
  const startEndTime = location.state?.startEndTime || null;
  const externalFilter = location.state?.externalFilter ?? null;
  const pageSize = location.state?.pageSize ?? null;
  const pageNumber = location.state?.pageNumber ?? null;
  const clickeduserEmail = location.state?.clickeduserEmail ?? null;
  const filterState = location.state?.filterState ?? null;
  const sort = location.state?.sort || null;
  const isAsk7gTab = location.state?.isAsk7GEnabled;
  const { showSnackbar } = useDisplaySnackbar();
  const navigate = useNavigate();
  const { id } = useParams();
  const is_team_meeting_id = true;
  const sharedWithMe = location.state?.sharedWithMe ?? selectedTabs === "sharedWithMe"? true : false;
  const keyword = location.state?.keyword || "";
  const sharedByMe = location.state?.sharedByMe ?? selectedTabs === "sharedByMe"? true : false;
  const isClientInteraction = location.state?.isClientInteraction ?? query.has("ci");
  const orgId = location.state?.orgId;
  const subOrgId = location.state?.subOrgId;
  const clientName = location?.state?.clientName;
  const themesActiveKey = location.state?.themesActiveKey;
  const accountViewState =
    location.state?.accountViewState ||
    (location.state?.selectedView || location.state?.selectedSpaces
      ? {
          selectedView: location.state?.selectedView,
          selectedSpaces: location.state?.selectedSpaces,
          selectedUserPorOrgId: location.state?.selectedUserPorOrgId,
        }
      : undefined);

  const [meetingData, setMeetingData] = useState<any | null>(null);
  const [_attendees, setAttendees] = useState<any | null>(null);
  const [meetingTimeText, setMeetingTimeText] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);
  const [notesCount, setNotesCount] = useState({
    PublicNotes: 0,
    PrivateNotes: 0,
  });
  const [collataralCount, setCollataralCount] = useState(0);
  const [attachmentCount, setAttachmentCount] = useState(0);
  const loggedInUserEmail = getUserAttributes()?.email;
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState(
    "Add Analyzer to the meeting"
  );
  const [openDialog, setOpenDialog] = useState(false);
  const [joinMeetingMode, setJoinMeetingMode] = useState<"live" | "upcoming" | null>(null);
  const [isMeetingOlder, setIsMeetingOlder] = useState(true);
  const { featureMap, accountDetails, impersonationDetails } = useContext(GlobalContext);
  const loggedInEmail = getUserAttributes()?.email;
  const [formattedDomainName, setFormattedDomainName] = useState("");
  const [isTruncated, setIsTruncated] = useState(false);
  const [topicsCount, setTopicsCount] = useState<number>(0);
  const titleRef = useRef<HTMLSpanElement | null>(null);
  const isMeetingOver = location.state?.isMeetingOver ?? query.has("imo");
  const hasMeetingTimePassed =
    meetingData?.endTime && new Date(meetingData.endTime) < new Date();
  const isRecordingDone =
    meetingData?.botStatusMessage?.toLowerCase()?.includes("recording is done.");
  const [isMeetingEnded, setIsMeetingEnded]= useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState(
    isMeetingOver ? "abstract-insights" : "meeting-details"
  ); // default to first child
  const [actionItemsCount, setActionItemsCount] = useState(0);
  const [slidesCount, setSlidesCount] = useState(0);
  const { pipState, setPipState } = useGlobalContext();
  const { resumeTabIndex } = pipState;
  const isExternalUser = accountDetails?.isExternalUser ?? false;
  const [isInteractionAttendee, setIsInteractionAttendee] =
    useState<boolean>(false);
  const [callQualityCount, setCallQualityCount] = useState(0);
  const [callQualityData, setCallQualityData] = useState<CallQualityData | any>(null);
  const [unauthorizedPerson, setUnauthorizedPerson] = useState(false);
  const [summaryByTopic, setSummaryByTopic] = useState<any[]>([]);
  const isAsk7GFeatureEnabled = featureMap?.idp_ask_7g === true;
  const isIdpHardDeleteEnabled = featureMap?.idp_assets_hard_delete_button === true;
  const [showAsk7GTab, setShowAsk7GTab] = useState<boolean>(false);
  const shareInteractionEnabled = featureMap?.share_interaction === true;
  const queryClient = useQueryClient();
  const [isAnalyzerLoading, setIsAnalyzerLoading] = useState(false);
  const now = new Date();
  const meetingStartTime = meetingData?.startTime
    ? new Date(meetingData.startTime)
    : null;

  const isMeetingNotStarted = meetingStartTime && now < meetingStartTime;
  const isMeetingStarted = meetingStartTime && now >= meetingStartTime;

  const botStatus = meetingData?.botStatusMessage?.toLowerCase() || "";
  const isAnalyzerScheduled = botStatus.includes("analyzer is scheduled");
  const [isShareOpen, setIsShareOpen] = useState<boolean>(false);
  const [hardDeleteCustomConfirm, setHardDeleteCustomConfrim] = useState<boolean>(false);
  const [isHardDeleting, setHardDeleting] = useState<boolean>(false);
  const [selectedRecording, setSelectedRecording] = useState<string | null>(null);
  const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
  const accessLevel = impersonationDetails?.accessType;
  const isImpersonating = impersonationDetails?.isImpersonating === true;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [frameworkMode, setFrameworkMode] =
  useState<"default" | "BANT" | "MEDDICC">("default");
  const isTemplateBasedExtarctionEnabled = featureMap?.template_based_extraction === true;
  const [resumeTimes, setResumeTimes] = useState<Record<string, number>>({});
  const shouldShowTemplateBasedExtraction = meetingData?.shouldShowTemplateBasedExtraction === true;

  const handleExtractionClick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    if (!isTemplateBasedExtarctionEnabled || !shouldShowTemplateBasedExtraction) {
      return;
    }
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);

  const { mutate: analyzerAction } = ManualBotService.useAnalyzerAction({
    onMutate: () => {
      setIsAnalyzerLoading(true);
    },
    onSuccess: (data) => {
      showSnackbar("success", data.message || "Analyzer action successful");

      queryClient.invalidateQueries({
        queryKey: ["get_interaction_details"],
      });
    },
    onError: (err) => {
      console.error("Analyzer action failed:", err);
      showSnackbar("error", "Failed to perform analyzer action");
    },
    onSettled: () => {
      setIsAnalyzerLoading(false);
    },
  });

  const handleAnalyzerAction = () => {
    if (!meetingData?.interactionId) return;

    if (isAnalyzerScheduled) {
      analyzerAction({
        interactionId: meetingData.interactionId,
        analyzerAction: "deschedule",
      });
      return;
    }

    if (!meetingData?.joinLink) {
      setJoinMeetingMode("upcoming");
      setOpenDialog(true);
      return;
    }

    analyzerAction({
      interactionId: meetingData.interactionId,
      analyzerAction: "schedule",
    });
  };

  const { mutate: removeAnalyzer } = ManualBotService.useRemoveAnalyzer({
    onMutate: () => {
      setIsAnalyzerLoading(true);
    },
    onSuccess: (data) => {
      showSnackbar("success", data.message || "Analyzer removed successfully");

      queryClient.invalidateQueries({
        queryKey: ["get_interaction_details"],
      });
    },
    onError: () => {
      showSnackbar("error", "Failed to remove analyzer");
    },
    onSettled: () => {
      setIsAnalyzerLoading(false);
    },
  });

  const { mutate: deleteAssetByGroup } = CollateralService.useDeleteAssetByGroup({
    onMutate: () => {
      setHardDeleting(true);
    },
    onSuccess: (data: any) => {
      showSnackbar(
        "success",
        data?.data?.message || "Recording deleted successfully"
      );
      setHardDeleteCustomConfrim(false);
      queryClient.invalidateQueries({ queryKey: ["get_interaction_collateral"] });
      setSelectedRecording(null);
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.error ||
        "Failed to delete recording";
      showSnackbar("error", msg);
    },
    onSettled: () => {
      setHardDeleting(false);
    },
  });

  const {
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    data: responseMeetingData,
  } = UserInteractionsService.useGetInteractionDetails(
    {
      interactionId: id || "",
      isTeamInteraction: is_team_meeting_id,
      sharedWithMe: sharedWithMe || undefined,
      sharedByMe: sharedByMe || undefined,
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
    },
    true
  );

  const {
    isLoading: isMeetingNotesLoading,
    isFetching: isMeetingNotesFetching,
    isSuccess: isMeetingNotesSuccess,
    isError: isMeetingNotesError,
    error: MeetingNotesError,
    data: MeetingNotesData,
  } = NotesService.useGetInteractionNotes(
    {
      interactionId: id || "",
      isTeamInteraction: is_team_meeting_id,
      sharedWithMe: sharedWithMe || undefined,
      sharedByMe: sharedByMe || undefined,
      isForOrg: isClientInteraction || undefined,
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
    },
    true
  );

  const {
    isLoading: isCollateralListLoading,
    isFetching: isCollateralListFetching,
    isSuccess: isCollateralListSuccess,
    isError: isCollateralListError,
    error: collateralListError,
    data: MeetingCollateralData,
  } = CollateralService.useGetInteractionCollateral(
    {
      interactionId: id || "",
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
    },
    id ? true : false
  );

  const { isSuccess: isInsightsSuccess, data: insightsData } =
    UserInteractionsService.useGetConversationInsights(
      {
        interactionId: id || "",
        groupId: selectedRecording || undefined,
        impersonatingPorId: impersonatingUserPorId,
        accessLevel: accessLevel,
      },
      !!id
    );


    const groupedRecordings = useMemo(() => {
    const groups: Record<string, any[]> = {};
    const files = Array.isArray(MeetingCollateralData?.data?.collaterals) ? MeetingCollateralData?.data?.collaterals : [];

    files.forEach((item: any) => {
      if (!item.interactionGroupId) return;

      if (!groups[item.interactionGroupId]) {
        groups[item.interactionGroupId] = [];
      }

      groups[item.interactionGroupId].push(item);
    });

    return groups;
  }, [MeetingCollateralData]);

  const groupStatusMap = useMemo(
    () => getGroupStatusMap(MeetingCollateralData?.data?.collaterals ?? []),
    [MeetingCollateralData]
  );

  const recordingGroups = useMemo(() => {
    let audioCount = 0;
    let videoCount = 0;
    
    // Filter groups: check assetSource as Account first -> if Account check aiAnalysisStatus. If not Account, group by groupId directly
    const entries = Object.entries(groupedRecordings).filter(
      ([groupId, items]) => {
        const hasAccountAsset = items.some((item: any) => {
          const src = (item.assetSource)?.toLowerCase();
          return src === "account";
        });

        if (hasAccountAsset) {
          const statuses = groupStatusMap[groupId] ?? [];
          return isGroupAiCompleted(statuses);
        }

        // For non-Account assets (recordings), group by groupId directly
        return true;
      }
    );

    const isRecordingGroup = (items: any[]) =>
      items.some((item: any) => {
        const src = item.assetSource?.toLowerCase();
        return (
          src === "7g_recall" ||
          src === "zoom" ||
          (item.assetType === "audio" && src === "7g_mobile_app") ||
          (item.assetName?.toLowerCase().endsWith(".mp4") && src !== "account")
        );
      });

    // Sort entries so meeting recordings (Recording 1, 2, 3, etc.) appear FIRST and others appear AFTER
    entries.sort(([_, itemsA], [__, itemsB]) => {
      const isRecA = isRecordingGroup(itemsA);
      const isRecB = isRecordingGroup(itemsB);
      if (isRecA && !isRecB) return -1;
      if (!isRecA && isRecB) return 1;
      return 0;
    });

    const totalAudio = entries.filter(([_, items]) =>
      items.some((item: any) => {
        const assetSource = item.assetSource?.toLowerCase();
        return (
          item.assetType === "audio" &&
          assetSource === "7g_mobile_app"
        );
      })
    ).length;

    const totalVideo = entries.filter(([_, items]) =>
      items.some((item: any) => {
        const assetSource = item.assetSource?.toLowerCase();
        return (
          item.assetName?.toLowerCase().endsWith(".mp4") &&
          (assetSource === "7g_recall" || assetSource === "zoom")
        );
      })
    ).length;

    return entries.map(([groupId, items], index) => {
      const isAudio = items.some(
        (item: any) =>
          item.assetType === "audio" && item.assetSource === "7g_mobile_app"
      );

      let label = "";
      const accountAsset = items.find((item: any) => {
        const fileExtension = getFileExtension(item.assetName || "");

        return (
          item.assetSource?.toLowerCase() === "account" &&
          item.assetName &&
          aiSupportedExtensions.includes(fileExtension.toLowerCase())
        );
      });

      if (accountAsset) {
        label = accountAsset.assetName;
      } else if (isAudio) {
        audioCount++;
        label = totalAudio > 1 ? `Audio Recording ${audioCount}` : "Audio Recording";
      } else {
        videoCount++;
        label = totalVideo > 1 ? `Recording ${videoCount}` : `Recording ${index + 1}`;
      }

      return {
        groupId,
        label,
        duration: items.find(
          (item: any) => typeof item?.assetMetaData?.transcriptDuration === "number"
        )?.assetMetaData?.transcriptDuration ?? null,
        items,
      };
    });
  }, [groupedRecordings, groupStatusMap]);

  useEffect(() => {
    if (recordingGroups.length > 0 && (!selectedRecording || !recordingGroups.some(g => g.groupId === selectedRecording))) {
      setSelectedRecording(recordingGroups[0].groupId);
    }
  }, [recordingGroups, selectedRecording]);

  const handleNavigateToRecording = (groupId?: string) => {
    if (groupId) {
      setSelectedRecording(groupId);
    }
    setSelectedTab("extract-callStats");
  };
  const condensedTranscriptAssetId = useMemo(() => {
    if (!selectedRecording || !groupedRecordings[selectedRecording]) return null;

    const assets = groupedRecordings[selectedRecording];

    const condensed = assets.find((item: any) =>
      item.assetType === "transcript" &&
      isCondensedTranscript(item.assetLocation)
    );

    return condensed?.interactionAssetId || null;
  }, [selectedRecording, groupedRecordings]);
    const {
    isLoading: isMeetingActionLoading,
    isFetching: isMeetingActionFetching,
    isSuccess: isMeetingActionSuccess,
    isError: isMeetingActionError,
    error: MeetingActionError,
    data: MeetingActionItemData,
  } = ActionItemService.useGetActionItems(
    {
      interactionId: id || "",
      isTeamInteraction: is_team_meeting_id,
      sharedWithMe: sharedWithMe || undefined,
      sharedByMe: sharedByMe || undefined,
      assetId: condensedTranscriptAssetId || undefined, 
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
    },
    true
  );

  const {
    data: slidesData,
    isSuccess: isSlidesSucess,
    isLoading: isSlidsLoading,
    isError: isSlidesError,
    error: slidesError,
  } = UserInteractionsService.useGetInteractionSlides(
    { interactionId: id || "",
      groupId: selectedRecording || undefined,
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
     },
    !!id
  );

  const {
    isLoading: isAbstractionSummaryInsightsLoading,
    isFetching:  isAbstractionSummaryInsightsFetching,
    isSuccess: isAbstractionSummaryInsightsSuccess,
    isError: isAbstractionSummaryInsightsError,
    error: AbstractionSummaryInsightsError,
    data: abstractionSummaryInsightsData,
  } = UserInteractionsService.useGetAbstractiveInsights({
    interactionId: id || "",
    groupId: selectedRecording || undefined,
    impersonatingPorId: impersonatingUserPorId,
    accessLevel: accessLevel,
  },
    !!id
  )

  const isPresentAsset = meetingData?.isPresentAsset;
  const isPreviousAsset = meetingData?.isPreviousAsset;
  useEffect(() => {
    if (isPresentAsset || isPreviousAsset || isMeetingOver || hasMeetingTimePassed || isRecordingDone) {
      setIsMeetingEnded(true);
    }
  }, [isMeetingEnded, isMeetingOver, hasMeetingTimePassed, isRecordingDone]);
  useEffect(() => {
    if (isSlidesSucess) {
      const allFrames: SlideFrame[] = slidesData?.data?.frames;

      const validFrames = allFrames?.filter(
        (frame) => frame?.s3_url && frame?.s3_url.trim() !== ""
      );
      setSlidesCount(validFrames?.length);
    }
    if (isSlidesError && selectedTab === "extract-slides") {
      const errorMessage =
        slidesError?.response?.data?.errors || "An unknown error occurred";
      showSnackbar("error", errorMessage);
    }
  }, [
    isSlidsLoading,
    isSlidesSucess,
    isSlidesError,
    slidesData,
    slidesError,
    selectedTab,
    showSnackbar,
  ]);

  useEffect(() => {
    if (isMeetingActionSuccess) {
      const responseData = MeetingActionItemData?.data?.data || {};
      const externalCount = responseData?.external?.length || 0;
      const internalCount = responseData?.internal?.length || 0;
      setActionItemsCount(externalCount + internalCount);
    }
    if (isMeetingActionError) {
      const errorMessage =
        MeetingActionError?.response?.data?.errors ||
        "An unknown error occurred";
      showSnackbar("error", errorMessage);
    }
  }, [
    isMeetingActionLoading,
    isMeetingActionFetching,
    isMeetingActionSuccess,
    isMeetingActionError,
  ]);

  useEffect(() => {
    setShowSpinner(isLoading);
    if (isSuccess) {
      const meeting = responseMeetingData;
      const loggedInParticipant = meeting?.attendedParticipants?.find(
        (participant: any) => participant?.email === loggedInEmail
      );
      setIsInteractionAttendee(loggedInParticipant?.email === loggedInEmail);

      setMeetingData(meeting);
      const allAttendees = meeting?.attendedParticipants;

      setAttendees(allAttendees);
      buttonVisibilityLogic(meeting);
      const clientName = (meeting?.clientDomains || [])
        .map((domain: string) => capitalizeFirstLetter(domain.split(".")[0]))
        .join(", ");
      setFormattedDomainName(clientName);
    }
    if (isError) {
      if(error?.response?.data?.message === "You are not allowed to view this interaction."){
        setUnauthorizedPerson(true);
      } else{
        showSnackbar("error", error?.response?.data?.message);
      }
    }
  }, [isLoading, isFetching, isSuccess, isError]);

  useEffect(() => {
    if (isMeetingNotesSuccess && Array.isArray(MeetingNotesData?.data)) {
      const privateNotes: NoteData[] = [];
      let publicNoteByLoggedInUser = 0;
      MeetingNotesData?.data?.forEach((note: any) => {
        if (!note) return;
        if (!note?.isPrivate) {
          publicNoteByLoggedInUser++;
        }
        if (note?.isPrivate) {
          if (note?.creatorEmail === loggedInUserEmail) privateNotes.push(note);
        }
      });

      setNotesCount({
        PrivateNotes: publicNoteByLoggedInUser,
        PublicNotes: 0,
      });
    }
    if (isMeetingNotesError) {
      const errorMessage =
        MeetingNotesError?.response?.data?.errors ||
        "An unknown error occurred";
      showSnackbar("error", errorMessage);
    }
  }, [
    isMeetingNotesLoading,
    isMeetingNotesFetching,
    isMeetingNotesSuccess,
    isMeetingNotesError,
  ]);

  useEffect(() => {
    if (meetingData?.startTime) {
      const startDate = new Date(meetingData.startTime);
      const formattedDate = startDate.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }); // e.g., "25 Jan 2025"
      const finalDate = formattedDate.replace(
        /(\d{2} \w{3}) (\d{4})/,
        "$1, $2"
      );

      setMeetingTimeText(finalDate);
    }
  }, [meetingData]);

  useEffect(() => {
    const ended =
      meetingData?.endTime && new Date(meetingData.endTime) < new Date();

    const isPresentAsset = meetingData?.isPresentAsset;
    const isPreviousAsset = meetingData?.isPreviousAsset;
       // Don't override if we came from Ask 7G tab
    if (isAsk7gTab) {
      setSelectedTab("ask-7g");
      return;
    }
    if (location.state?.selectedTab) {
      if (["private-notes"].includes(location.state.selectedTab)) {
        setSelectedTab(location.state.selectedTab);
        return;
      }
    }
    if (keyword) {
      setSelectedTab("extract-topics");
    } else {
      setSelectedTab(
        isPreviousAsset || isPresentAsset || ended ? "abstract-insights" : "meeting-details"
      );
    }
    if (resumeTabIndex !== null && meetingData) {
      setPipState((prev) => ({ ...prev, resumeTabIndex: null }));
    }
  }, [meetingData]);

  useEffect(() => {
    if (isCollateralListSuccess) {
      const result = Array.isArray(MeetingCollateralData?.data?.collaterals) ? MeetingCollateralData?.data?.collaterals : [];
      let recordingCount = 0;
      let attachementCount = 0;
      result?.forEach((obj: any) => {
        if (
          obj.assetType === "transcript" &&
          obj.assetLocation?.toLowerCase()?.endsWith(".mp4")
        ) {
          recordingCount++;
        }
        if (
          (obj.assetType === "other" || obj.assetSource === "MOM" || obj.assetType === "link")
        ) {
          attachementCount++;
        }
      });
      const collataralCount = recordingCount;

      setCollataralCount(collataralCount);
      setAttachmentCount(attachementCount);
    }
    if (isCollateralListError) {
      const errorMessage =
        collateralListError?.response?.data?.errors ||
        "An unknown error occurred";
      showSnackbar("error", errorMessage);
    }
  }, [
    isCollateralListLoading,
    isCollateralListFetching,
    isCollateralListSuccess,
    isCollateralListError,
  ]);

  useEffect(() => {
    if (isInsightsSuccess) {
      const response = insightsData?.data;
      const topics = response?.flatMap(
        (analytics: any) => analytics.summaryByTopic?.summary_by_topic || []
      );
      // Check if any item has overallSentiment
    const hasOverallSentiment = response?.some(
      (analytics: any) => !!analytics?.sentiment?.sentimentSummary
    );
    if (hasOverallSentiment) {
      setIsMeetingEnded(true);
    }
    setShowAsk7GTab(hasOverallSentiment);
      setSummaryByTopic(topics);
      setTopicsCount(topics.length ?? 0);
    }
  }, [isInsightsSuccess, insightsData]);

  const buttonVisibilityLogic = (meeting: any) => {
    if (meeting && meeting.startTime) {
      const botStatus = meeting?.botStatusMessage;
      const checkOlderDuration = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      const currentTime = new Date().getTime();
      const meetingStartTime = new Date(meeting.startTime).getTime();
      const isOlder = currentTime - meetingStartTime > checkOlderDuration;
      if (botStatus?.toLowerCase().includes("recording is done.")) {
        setIsMeetingOlder(false);
      } else if (isOlder) {
        setIsMeetingOlder(!isOlder); // Set to false if older
      } else {
        setIsButtonDisabled(false);
        setTooltipMessage("Add Analyzer to the live meeting");
      }
    } else {
      setIsButtonDisabled(true);
      setTooltipMessage("Meeting times are not available");
    }
  };

  useEffect(()=>{
    setShowSpinner(isAbstractionSummaryInsightsFetching || isAbstractionSummaryInsightsLoading)
    if(isAbstractionSummaryInsightsSuccess){
      const payload = abstractionSummaryInsightsData?.data?.payload;
    if (payload && typeof payload === "object" && Object.keys(payload).length > 0) {
          // Dynamically extract the role key (e.g. 'analyst', 'manager', etc.)
    const roleKey = payload ? Object.keys(payload)[0] : null;
    const insights = roleKey ? payload[roleKey] : null;

    if (insights && typeof insights === "object") {
      // Dynamically check all sections (opening, execution, etc.)
      const hasCallQuality = Object.values(insights).some((section: any) => {
        if (!section || typeof section !== "object") return false;
        const { questions, what_went_well, areas_of_opportunity } = section;
        return (
          (Array.isArray(questions) && questions.length > 0) ||
          (Array.isArray(what_went_well) && what_went_well.length > 0) ||
          (Array.isArray(areas_of_opportunity) && areas_of_opportunity.length > 0)
        );
      });

      setCallQualityCount(hasCallQuality ? 1 : 0);
      setCallQualityData(insights);
      return;
    } 
  }
    // If payload missing, roleKey missing, or insights invalid → reset
    setCallQualityCount(0);
    setCallQualityData(null);
  }
    if(isAbstractionSummaryInsightsError){
      const errorMessage = AbstractionSummaryInsightsError?.response?.data?.errors || "An unknown error occurred";
      showSnackbar("error", errorMessage);
    }
  },[abstractionSummaryInsightsData,
    isAbstractionSummaryInsightsFetching,
    isAbstractionSummaryInsightsLoading,
    isAbstractionSummaryInsightsSuccess,
    isAbstractionSummaryInsightsError,
  ])

  const handleAddBot = () => {
    setJoinMeetingMode("live");
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setJoinMeetingMode(null);
  };

  useEffect(() => {
    const element = titleRef.current;
    if (element) {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [meetingData?.title, formattedDomainName, meetingTimeText]);

  const fullContent = `${meetingData?.title || ""}${
    meetingData?.clientDomains?.length > 0 ? ` (${formattedDomainName})` : ""
  } ${meetingTimeText}`;

  useEffect(() => {
    setSlidesCount(0);
    setCallQualityCount(0);
    setCallQualityData(null);
    setSummaryByTopic([]);
    setTopicsCount(0);
  }, [selectedRecording]);

  const handleBackClick = () => {
    setSelectedTab("");
    setShowSpinner(false);   
    if (
      ((selectedNps !== null && selectedNps !== undefined) ||
        (startEndMeetingTimes !== null &&
          startEndMeetingTimes !== undefined)) &&
      location.state?.ParentPageIdentifier === "Client_Page"
    ) {
      navigate("/client", {
        state: {
          selectedNps,
          startEndMeetingTime,
          startEndMeetingTimes,
          newUserTeamData,
          tableFilters,
          clientName,
        },
      });
    } else if (location.state?.parent_page_identifier === "client-slideover") {
      navigate("/client", {
        state: {
          selectedNps,
          startEndMeetingTime,
          startEndMeetingTimes,
          newUserTeamData,
          tableFilters,
          isSlideOverOpen,
          clientDomain,
          slideOverParent,
          activeStat,
          customDateRange,
          orgId,
          subOrgId,
          clientName,
        },
      });
    } else if (
      startEndMeetingTime !== null &&
      startEndMeetingTime !== undefined &&
      location.state?.ParentPageIdentifier === "Action_Item_Page"
    ) {
      navigate("/actionItems", {
        state: {
          startEndMeetingTime,
          view,
          type,
          newUserTeamData,
        },
      });
    } else if (
      location.state?.startEndTime !== null &&
      location.state?.startEndTime !== undefined &&
      location.state?.ParentPageIdentifier === "Meeting_page" &&
      filterState?.currentRoute 
    ) {
      navigate(filterState?.currentRoute || "/meetings/my", {
        state: {
          selectedTabs,
          startEndTime,
          newUserTeamData,
          external,
          externalFilter,
          pageSize,
          pageNumber,
          tableFilters,
          filterState
        },
      });
    } else if (
      location.state?.parent_page_identifier === "analytics-interactions-page"
    ) {
      navigate("/analytics/interactions", {
        state: {
          parent_page_identifier: "analytics-interactions-page",
          selectedTabs,
          selectedAnalyticsTab:
            location.state?.selectedAnalyticsTab || location.state?.selectedTab,
          analyticsDateRange: location.state?.analyticsDateRange,
          interactionsState: location.state?.interactionsState,
          newUserTeamData,
          startEndMeetingTime,
          isSlideOverOpen,
          slideOverParent,
          isClientInteraction,
          is_team_meeting_id,
          selectedNps,
          customDateRange,
          clickeduserEmail,
          interactionType: location.state?.interactionType,
        },
      });
    } else if (location.state?.ParentPageIdentifier === "reportingPage") {
      navigate("/reporting", {
        state: {
          fromTab: location.state?.fromTab || "meetings",
          ParentPageIdentifier: "reportingPage",
          keyword: location.state?.keyword || "",
        },
      });
    } else if (
      location?.state?.parent_page_identifier === "Client_Page_interactions"
    ) {
      navigate(`/client/${location?.state?.clientDomain}`, {
        state: {
          clientDomain: location?.state?.clientDomain,
          isClientInteraction: location?.state?.isClientInteraction,
          clientName,
        },
      });
    } else if (location?.state?.ParentPageIdentifier === "AccountPage") {
      navigate("/accounts/customer");
    } else if (
      location?.state?.parent_page_identifier === "client-interaction-overview"
    ) {
      navigate("/client", {
        state: {
          startEndMeetingTime,
          startEndMeetingTimes,
          isSlideOverOpen,
          slideOverParent,
          isClientInteraction,
          is_team_meeting_id,
          appliedFilters,
          customDateRange,
          sort: sort,
          clientName,
          initialTab: location.state?.activeView,
          themesActiveKey,
        },
      });
    } 
    else if (location?.state?.parent_page_identifier === "account-customer")
      {
        navigate("/accounts/customer",{
          state: {
            ...location?.state,
          startEndMeetingTime,
          startEndMeetingTimes,
          isSlideOverOpen,
          slideOverParent,
          isClientInteraction,
          is_team_meeting_id,
          clientDomain: location?.state?.clientDomain,
          sort: location?.state?.sort,
          clientName,
          initialTab: location.state?.activeView,
          accountViewState,
          themesActiveKey,
          accountRelationshipType: accountViewState?.accountRelationshipType,
        },
        })
      } else if(location?.state?.parent_page_identifier === "account-vendor"){
        navigate("/accounts/vendor",{
          state: {
          ...location?.state,
          startEndMeetingTime,
          startEndMeetingTimes,
          isSlideOverOpen,
          slideOverParent,
          isClientInteraction,
          is_team_meeting_id,
          clientDomain: location?.state?.clientDomain,
          sort: location?.state?.sort,
          clientName,
          initialTab: location.state?.activeView,
          themesActiveKey
        },
        })
      }
      else if(location?.state?.parent_page_identifier === "account-prospect"){
        navigate("/accounts/prospect",{
          state: {
          ...location?.state,
          startEndMeetingTime,
          startEndMeetingTimes,
          isSlideOverOpen,
          slideOverParent,
          isClientInteraction,
          is_team_meeting_id,
          clientDomain: location?.state?.clientDomain,
          sort: location?.state?.sort,
          clientName,
          initialTab: location.state?.activeView,
          themesActiveKey
        },
        })
      } else if(location?.state?.parent_page_identifier === "account-spaces"){
        navigate("/accounts/spaces",{
          state: {
          ...location?.state,
          startEndMeetingTime,
          startEndMeetingTimes,
          isSlideOverOpen,
          slideOverParent,
          isClientInteraction,
          is_team_meeting_id,
          clientDomain: location?.state?.clientDomain,
          sort: location?.state?.sort,
          clientName,
          initialTab: location.state?.activeView,
          accountViewState,
          themesActiveKey,
          accountRelationshipType: accountViewState?.accountRelationshipType,
        },
        })
      } else if(location?.state?.parent_page_identifier === "contacts") {
      navigate("/contacts",{
        state: {
          startEndMeetingTime,
          startEndMeetingTimes,
          isSlideOverOpen,
          slideOverParent,
          isClientInteraction,
          is_team_meeting_id,
          userEmail,
          profileOrgId,
        }
      });
    } else if(location?.state?.parent_page_identifier === "user-analytics"){
      navigate(`/analytics/${userName}`,{
        state: {
          startEndMeetingTime,
          startEndMeetingTimes,
          isSlideOverOpen,
          slideOverParent,
          isClientInteraction,
          is_team_meeting_id,
          userName,
          clientDomain,
          userDetails,
          email: userDetails?.userEmail,
        }
      });
    } else if (
      location?.state?.parent_page_identifier === "opportunities"
    ) {
      navigate("/opportunities", {
        state: {
          ...location.state,
          opportunityNavigationState:
            location.state?.opportunityNavigationState || location.state,
        },
      });
    } else if(location?.state?.parent_page_identifier === "action-items"){
      navigate("/tasks/all-tasks")
    }
    else {
      navigate("/meetings/my");
    }
  };
  const isSelected = [
    "abstract-insights",
    "extract-topics",
    "extract-actionItems",
    "extract-slides",
    "extract-callStats",
    "BANT-analysis",
    "MEDDICC-analysis"
  ].includes(selectedTab);

  const extractionTabs = [
    // Accordion-style Extraction Header
    <Box
      key="extract-header"
      className={
        isSelected && isTemplateBasedExtarctionEnabled && shouldShowTemplateBasedExtraction
          ? "extract-header-extraction"
          : "extract-header"
      }
      onClick={handleExtractionClick}
      sx={{ marginBottom: "5px" }}
    >
      <TabHeader
        label={
          frameworkMode === "BANT"
            ? "BANT"
            : frameworkMode === "MEDDICC"
            ? "MEDDICC"
            : "Extraction"
        }
        count={0}
        defaultIcon={<img src={DefaultExtractiveSummyIcon} alt="default" />}
        selectedIcon={<img src={SelectedExtractiveSummyIcon} alt="selected" />}
        isSelected={[
          "abstract-insights",
          "extract-topics",
          "extract-actionItems",
          "extract-slides",
          "extract-callStats",
          "BANT-analysis",
          "MEDDICC-analysis"
        ].includes(selectedTab)}
      />
      {isTemplateBasedExtarctionEnabled && shouldShowTemplateBasedExtraction &&
        (open ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        ))}
      {isTemplateBasedExtarctionEnabled && shouldShowTemplateBasedExtraction &&(
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        sx={{ zIndex: 1300 }}
      >
        <Paper className="extraction-popper">
          {frameworkMode !== "BANT" && (
            <MenuItem
              onClick={() => {
                setFrameworkMode("BANT");
                setSelectedTab("BANT-analysis");
                setAnchorEl(null);
              }}
            >
              BANT
            </MenuItem>
          )}

          {frameworkMode !== "MEDDICC" && (
            <MenuItem
              onClick={() => {
                setFrameworkMode("MEDDICC");
                setSelectedTab("MEDDICC-analysis");
                setAnchorEl(null);
              }}
            >
              MEDDICC
            </MenuItem>
          )}

          {frameworkMode !== "default" && (
            <MenuItem
              onClick={() => {
                setFrameworkMode("default");
                setSelectedTab("abstract-insights");
                setAnchorEl(null);
              }}
            >
              Extraction
            </MenuItem>
          )}
        </Paper>
      </Popper>
      )}
    </Box>,

          <Tab
            key="abstract-insights"
            value="abstract-insights"
            className="highlight-tab extract-sub-tab"
            label={
              <Box className="tab-label-box">
                <TabHeader
                  label="Summary"
                  count={0}
                  inlineCount
                  isSelected={selectedTab === "abstract-insights"}
                />
              </Box>
            }
          />,
          <Tab
            key="topics"
            value="extract-topics"
            className="highlight-tab extract-sub-tab"
            label={
              <Box className="tab-label-box">
                <TabHeader
                  label="Topics"
                  count={topicsCount}
                  inlineCount
                  isSelected={selectedTab === "extract-topics"}
                />
              </Box>
            }
          />,
          <Tab
            key="actionItems"
            value="extract-actionItems"
            className="highlight-tab extract-sub-tab"
            label={
              <Box className="tab-label-box">
                <TabHeader
                  label="Action Items"
                  count={actionItemsCount}
                  inlineCount
                  isSelected={selectedTab === "extract-actionItems"}
                />
              </Box>
            }
          />,
          <Tab
            key="slides"
            value="extract-slides"
            className="highlight-tab extract-sub-tab"
            label={
              <Box className="tab-label-box">
                <TabHeader
                  label="Slides"
                  count={slidesCount}
                  inlineCount
                  isSelected={selectedTab === "extract-slides"}
                />
              </Box>
            }
          />,
          <Tab
            key="callStats"
            value="extract-callStats"
            className="highlight-tab extract-sub-tab"
            label={
              <Box className="tab-label-box">
                <TabHeader
                  label="Recording"
                  count={0}
                  inlineCount
                  isSelected={selectedTab === "extract-callStats"}
                />
              </Box>
            }
          />,

    // Abstraction header (replaces the old single Tab)
  !isExternalUser && callQualityCount > 0 && (
    <Box
      key="abstract-header"
      className="extract-header"
      sx={{ marginBottom: "12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}
    >
      <TabHeader
        label="Abstraction"
        count={0}
        defaultIcon={<img src={DefaultAbstractivSummyIcon} alt="default" />}
        selectedIcon={<img src={SelectedAbstractivSummyIcon} alt="selected" />}
        // consider selected if either of the abstraction sub-tabs are active
        isSelected={["abstract-callQuality"].includes(selectedTab)}
      />
    </Box>
  ),

    [
        ...(callQualityCount > 0
          ?[ <Tab
          key="abstract-callQuality"
          value="abstract-callQuality"
          className="highlight-tab extract-sub-tab"
          label={
            <Box className="tab-label-box">
              <TabHeader
                label="Call Scoring"
                count={0}
                inlineCount
                isSelected={selectedTab === "abstract-callQuality"}
              />
            </Box>
          }
        />
      ] : []),
      ],

    <Box
      key="assets-header"
      className="extract-header"
      sx={{ marginBottom: "12px" }}
    >
      <TabHeader
        label="Collaterals"
        count={collataralCount + attachmentCount}
        defaultIcon={<img src={DefaultCollateralsIcon} alt="default" />}
        selectedIcon={<img src={SelectedCollateralsIcon} alt="selected" />}
        isSelected={["assets-attachments"].includes(
          selectedTab
        )}
      />
    </Box>,
        [
          <Tab
            key="assets-attachments"
            value="assets-attachments"
            className="highlight-tab extract-sub-tab"
            label={
              <Box className="tab-label-box">
                <TabHeader
                  label="Attachments"
                  count={attachmentCount}
                  inlineCount
                  isSelected={selectedTab === "assets-attachments"}
                />
              </Box>
            }
          />,
        ],

    !isExternalUser && (
      <Box
        key="notes-header"
        className="extract-header"
        sx={{ marginBottom: "12px" }}
      >
        <TabHeader
          label="Planning"
          count={
            (notesCount?.PublicNotes ?? 0) + (notesCount?.PrivateNotes ?? 0)
          }
          defaultIcon={<img src={DefaultNotesIcon} alt="default" />}
          selectedIcon={<img src={SelectedNotesIcon} alt="selected" />}
          isSelected={["public-notes", "private-notes"].includes(selectedTab)}
        />
      </Box>
    ),

        [
          <Tab
            key="public-notes"
            value="public-notes"
            className="highlight-tab extract-sub-tab"
            label={
              <Box className="tab-label-box">
                <TabHeader
                  label="Past-to-Present"
                  count={notesCount?.PublicNotes}
                  inlineCount
                  isSelected={selectedTab === "public-notes"}
                />
              </Box>
            }
          />,
        ],
  ];
  {
    isAsk7GFeatureEnabled && showAsk7GTab && (

      extractionTabs.push(
        <Box
        key="ask-7g-header"
        onClick={() => {
          setSelectedTab("ask-7g");
        }}
        className="extract-header"
        sx={{ marginBottom: "12px" }}
        >
        <TabHeader
          label="Ask 7G"
          count={0} // optional, can remove if not needed
          defaultIcon={<img src={SelectedAsk7GIcon} alt="default" />}
          selectedIcon={<img src={SelectedAsk7GIcon} alt="selected" />}
          isSelected={selectedTab === "ask-7g"}
          isTextHighlighted={selectedTab === "ask-7g"}
          />
      </Box>
    )
  )}
  const meetingDetailsTabs = [
    <Tab
      key="meeting-details"
      value="meeting-details"
      className="tab-meeting-details"
      label={
        <TabHeader
          label="Interaction details"
          count={0}
          defaultIcon={<img src={DefaultExtractiveSummyIcon} alt="default" />}
          selectedIcon={
            <img src={SelectedExtractiveSummyIcon} alt="selected" />
          }
          isSelected={selectedTab === "meeting-details"}
        />
      }
    />,
    !isExternalUser && (
      <Box
        key="notes-header"
        className="extract-header"
        sx={{ marginBottom: "12px" }}
      >
        <TabHeader
          label="Planning"
          count={
            (notesCount?.PublicNotes ?? 0) + (notesCount?.PrivateNotes ?? 0)
          }
          defaultIcon={<img src={DefaultNotesIcon} alt="default" />}
          selectedIcon={<img src={SelectedNotesIcon} alt="selected" />}
          isSelected={["public-notes", "private-notes"].includes(selectedTab)}
        />
      </Box>
    ),

    // Notes tabs
        [
          <Tab
            key="public-notes"
            value="public-notes"
            className="highlight-tab extract-sub-tab"
            label={
              <Box className="tab-label-box">
                <TabHeader
                  label="Past-to-Present"
                  count={notesCount?.PublicNotes}
                  inlineCount
                  isSelected={selectedTab === "public-notes"}
                />
              </Box>
            }
          />,
          <Tab
            key="private-notes"
            value="private-notes"
            className="highlight-tab extract-sub-tab"
            label={
              <Box className="tab-label-box">
                <TabHeader
                  label="Prep Notes"
                  count={notesCount?.PrivateNotes}
                  inlineCount
                  isSelected={selectedTab === "private-notes"}
                />
              </Box>
            }
          />,
        ],
  ];

  const frameworkTabs = [
    extractionTabs[0],
    <Tab
      key={`${frameworkMode}-analysis`}
      value={`${frameworkMode}-analysis`}
      className="highlight-tab extract-sub-tab"
      label={
        <Box className="tab-label-box">
          <TabHeader
            label="Analysis"
            count={0}
            inlineCount
            isSelected={
              selectedTab === `${frameworkMode}-analysis`
            }
          />
        </Box>
      }
    />,
  ];

  // Add a condition to render tabs only after the API call is completed
  if (showSpinner) {
    return (
      <Box className="meetingParent">
        <ProgressSpinner showSpinner={true} />
      </Box>
    );
  }
  if (unauthorizedPerson) {
    return (
      <Box className="meetingParent">
        <Box className="meetingBody">
            <UnauthorizedAccess
            title = "You do not have permission to access this Interaction"
            message="Please ask the user to share the interaction, via 7thGear app."
            onClick={handleBackClick}
            />
        </Box>
      </Box>
    )
  }

  const handleCopy = async (tab: string) => {
    let formattedText = "";
    if (tab === "extract-topics") {
      formattedText = getTopicSummaryHTML(summaryByTopic);
    } else if (tab === "abstract-insights") {
      formattedText = getSentimentHTML(insightsData?.data);
      const firstInsight = insightsData?.data?.[0];
      const keyTakeaways =
        firstInsight?.keyTakeaways ?? firstInsight?.key_takeaways ?? [];
      if (Array.isArray(keyTakeaways) && keyTakeaways.length > 0) {
        formattedText += getKeyTakeawaysHTML(keyTakeaways);
      }
    } else if (tab === "abstract-callQuality") {
      formattedText = getCallQualityHTML(callQualityData);
    } else if (tab === "extract-actionItems") {
        const responseData = MeetingActionItemData?.data?.data || {};
        const allActionItems = [
          ...(responseData?.external || []),
          ...(responseData?.internal || []),
        ];
        formattedText = getActionItemsHTML(allActionItems);
      }
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([formattedText], { type: "text/html" }),
          "text/plain": new Blob([formattedText.replace(/<[^>]+>/g, "")], { type: "text/plain" })
        })
      ]);
    } catch {
      showSnackbar("error", "Failed to copy.");
    }
  };

  return (
    <>
      <Box className="meetingParent"
      sx={{paddingTop: isImpersonating ? "20px" : ""}}>
        {/* HEADER */}
        <Box className="meetingHeader">
          <Box className="flex flex-row items-center w-full">
            {/* Back Button */}
            <Box
              className="flex flex-row justify-start items-center gap-1 cursor-pointer pl-3"
              onClick={handleBackClick}
            >
              <Box className="back-icon-button">
                <Tooltip title="Go back" placement="top" arrow>
                  <img src={ArrowBackIcon} />
                </Tooltip>
              </Box>
            </Box>

            {/* Title + Status (KEEPING YOUR PART AS IS) */}
            <Box className="flex flex-row items-center justify-between" sx={{ flex: "1 1 auto", minWidth: 0 }}>
              {/* Title and Time */}
              <Tooltip
                title={isTruncated ? fullContent : ""}
                disableHoverListener={!isTruncated}
              >
                <Box
                  className="meeting-title-row"
                  sx={{
                    cursor: isTruncated ? "pointer" : "default",
                  }}
                >
                  <EllipsedTooltip
                    className="meeting-title-text"
                    maxWidth="100%"
                    tooltipContent={meetingData?.title}
                  >
                    {meetingData?.title || ""}
                  </EllipsedTooltip>

                  {meetingData?.clientDomains?.length > 0 && (
                    <Typography
                      component="span"
                      className="domain-text"
                    >
                      ({formattedDomainName})
                    </Typography>
                  )}

                  <Typography
                    component="span"
                    className="date-text"
                  >
                    - {meetingTimeText}
                  </Typography>
                  {recordingGroups?.length > 0 && 
                  <Divider orientation="vertical" sx={{border: "1px solid #7A7A8C", height: "22px"}}/>
                  }
                  <Box className="recording-button-box">
                    {recordingGroups.map((rec) => {
                      const isSelected = selectedRecording === rec.groupId;
                      const durationLabel =
                        typeof rec.duration === "number"
                          ? secondsToHHMMSS(rec.duration).replace(/^00:/, "")
                          : null;

                      return (
                        <button
                          key={rec.groupId}
                          onClick={() => { 
                            setSelectedRecording(rec.groupId); 
                            if (frameworkMode === "BANT" || frameworkMode === "MEDDICC") {
                              return;
                            }
                            setSelectedTab("extract-callStats")}}
                          className="recording-buttons"
                          style={{
                            backgroundColor: isSelected ? "#6036D6" : "#FFFFFF",
                            color: isSelected ? "#FFF" : "#575763",
                            border: isSelected ? "#6036D6" : "1px solid #D6D6DB",
                            boxShadow: isSelected ? "0px 4px 4px 0px #00000040" : "",
                          }} 
                        >
                          <EllipsedTooltip
                            tooltipContent={rec.label}
                            maxWidth="80px"
                          >
                            {rec.label}
                          </EllipsedTooltip>
                          <span style={{ color: "#D6D6DB", fontWeight: 400, paddingLeft: "4px" }}>{durationLabel ? ` [${durationLabel}]` : ""}</span>
                        </button>
                      );
                    })}
                  </Box>
                </Box>
              </Tooltip>

              {/* Right icons */}
              {!isImpersonating && (
              <Box className="flex flex-row items-center gap-4 pr-6" sx={{ flexShrink: 0 }}>
                {(selectedTab === "extract-topics" && topicsCount > 0 ||
                  selectedTab === "abstract-insights" && insightsData?.data?.length > 0 ||
                  selectedTab === "abstract-callQuality" && callQualityCount > 0 ||
                  selectedTab === "extract-actionItems" && actionItemsCount > 0) && (
                  <Box
                    className="flex flex-row justify-end w-fit"
                    sx={{
                      marginTop: "-2px",
                      cursor: "pointer",
                      marginRight: "10px",
                    }}
                  >
                    <Tooltip title={tooltipTitles[selectedTab] || "Copy"} arrow>
                      <img
                        src={CopySvg}
                        onClick={async () => {
                          try {
                            await handleCopy(selectedTab);
                            showSnackbar("success", "Copied to clipboard!");
                          } catch (err) {
                            showSnackbar("error", "Failed to copy to clipboard.");
                          }
                        }}
                        style={{ display: "block", cursor: "pointer" }}
                      />
                    </Tooltip>
                  </Box>
                )}
                {shareInteractionEnabled && (isPresentAsset || isPreviousAsset) && (
                  <Box
                    onClick={() => setIsShareOpen(true)}
                    style={{
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <Tooltip title="Share" arrow>
                      <img src={ShareIcon} alt="Share" style={{height: "24px", width: "24px"}} />
                    </Tooltip>
                  </Box>
                )}
                {isIdpHardDeleteEnabled && recordingGroups?.length > 0 && selectedRecording && (
                  <Box
                    onClick={() => {
                        setHardDeleteCustomConfrim(true);
                    }}
                    className="idp_hard_delete_box"
                  >
                    <Tooltip title={"Delete Asset"} arrow>
                      <img src={DeleteIcon} alt="Delete" />
                    </Tooltip>
                  </Box>
                )}

                {meetingData && isMeetingOlder && (
                  <>
                    {/* BEFORE MEETING START */}
                    {isMeetingNotStarted && (
                      <Tooltip
                        title={
                          isAnalyzerScheduled
                            ? "Unschedule analyzer for this meeting"
                            : "Schedule analyzer for this meeting"
                        }
                        arrow
                      >
                        <span>
                          <Button
                            variant="contained"
                            size="small"
                            className={`addAnalyzerBtn ${isAnalyzerScheduled ? "removeAnalyzerBtn" : ""}`}
                            onClick={handleAnalyzerAction}
                            disabled={isAnalyzerLoading}
                            startIcon={
                              <Box
                                component="span"
                                className={`analyzer_btn_logo_wrapper ${isAnalyzerScheduled ? "strikeIcon" : ""}`}
                              >
                                <Box
                                  component="img"
                                  src={isAnalyzerScheduled ? logo7GMagenta : logo7G}
                                  alt=""
                                  className="analyzer_btn_logo"
                                />
                              </Box>
                            }
                          >
                            {isAnalyzerScheduled
                              ? "Unschedule Analyzer"
                              : "Schedule Analyzer"}
                          </Button>
                        </span>
                      </Tooltip>
                    )}

                    {/* AFTER MEETING START */}
                    {isMeetingStarted && (
                      <>
                        {hasMeetingTimePassed && (
                          <Tooltip title={tooltipMessage} placement="top" leaveDelay={1000}>
                            <span>
                              <Button
                                variant="contained"
                                size="small"
                                className="addAnalyzerBtn"
                                onClick={handleAddBot}
                                disabled={isButtonDisabled}
                                startIcon={
                                  <Box
                                    component="img"
                                    src={logo7G}
                                    className="analyzer_btn_logo"
                                    alt=""
                                  />
                                }
                              >
                                Add Analyzer
                              </Button>
                            </span>
                          </Tooltip>
                        )}

                        {/* MEETING RUNNING */}
                        {!hasMeetingTimePassed && (
                          <>
                            {!isAnalyzerScheduled && (
                              <Tooltip title={tooltipMessage} placement="top" leaveDelay={1000}>
                                <span>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    className="addAnalyzerBtn"
                                    onClick={handleAddBot}
                                    disabled={isButtonDisabled}
                                    startIcon={
                                      <Box
                                        component="img"
                                        src={logo7G}
                                        className="analyzer_btn_logo"
                                        alt=""
                                      />
                                    }
                                  >
                                    Add Analyzer
                                  </Button>
                                </span>
                              </Tooltip>
                            )}

                            {isAnalyzerScheduled && (
                              <Tooltip title="Remove analyzer from this meeting" arrow>
                                <span>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    className="addAnalyzerBtn removeAnalyzerBtn"
                                    onClick={() => removeAnalyzer(meetingData.interactionId)}
                                    disabled={isAnalyzerLoading}
                                    startIcon={
                                      <Box
                                        component="span"
                                        className="analyzer_btn_logo_wrapper strikeIcon"
                                      >
                                        <Box
                                          component="img"
                                          src={logo7GMagenta}
                                          className="analyzer_btn_logo"
                                          alt=""
                                        />
                                      </Box>
                                    }
                                  >
                                    Remove Analyzer
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* BODY */}
        <Box className="meetingBody"
        sx={{background: selectedTab === "ask-7g" ? "linear-gradient(180deg, #fff9ff, #fef6ff)" : ""}}
        >
          {/* Sidebar */}
          <Box
            className={`meetingSidebar ${!isMeetingEnded ? "withMinHeight" : ""}`}
          >
            <Tabs
              orientation="vertical"
              value={selectedTab}
              onChange={(_e, newValue) => setSelectedTab(newValue)}
              variant="scrollable"
              TabIndicatorProps={{ style: { display: "none" } }}
              className="vertical-tabs"
            >
              {["BANT", "MEDDICC"].includes(frameworkMode)
                ? frameworkTabs
                : isMeetingEnded
                ? extractionTabs
                : meetingDetailsTabs}
            </Tabs>
          </Box>

          {/* Content */}
          <Box
            className={
              ["extract-slides", "extract-topics", 
                "assets-attachments", "public-notes",
              "extract-actionItems", "abstract-callQuality", "abstract-insights", "private-notes", "extract-callStats" ].includes(selectedTab)
                ? "meetingContent extractContent"
                : "meetingContent"
            }
          >
            {/* All your conditional tab content here */}
            {selectedTab === "extract-slides" && (
              <SlidesPage interactionId={id || ""} slidesData={slidesData}/>
            )}
            {selectedTab === "extract-topics" && (
              <TopicsPage interactionId={id || ""} keyword={keyword} groupId={selectedRecording}/>
            )}
            {selectedTab === "extract-actionItems" && (
              <InteractionActionItems 
              interactionId={id || ""}
              interactionDetails ={meetingData}
              isInteractionAttendee = {isInteractionAttendee}
              assetId={condensedTranscriptAssetId || ""}
               />
            )}
            {selectedTab === "meeting-details" && !isMeetingEnded && (
              <Meeting meeting={meetingData} isInteractionAttendee = {isInteractionAttendee} />
            )}
            {selectedTab === "extract-callStats" && (
              <CallStatsPage
                interactionId={id || ""}
                meetingData={meetingData?.attendedParticipants}
                interactionDetails ={meetingData}
                collateralData={MeetingCollateralData}
                isTeamInteraction={is_team_meeting_id}
                isInteractionAttendee = {isInteractionAttendee}
                groupId={selectedRecording}
                resumeTimes={resumeTimes}
                setResumeTimes={setResumeTimes}
              />
            )}
            {selectedTab === "abstract-insights" && (
              <AbstractiveSummaryPage interactionId={id || ""} insightsData={insightsData}/>
            )}
            {selectedTab === "abstract-callQuality" && (
              <InteractionCallQuality 
              callQualityData={callQualityData}
              interactionId={id || ""}
              />)}
            {selectedTab === "assets-attachments" && (
              <Box className="collateralWrapper">
                <Collaterals
                  meetingId={id || ""}
                  type="other"
                  selectedTab={selectedTab}
                  isInteractionAttendee={isInteractionAttendee}
                  interactionDetails ={meetingData}
                  onNavigateToRecording={handleNavigateToRecording}
                />
              </Box>
            )}
            {selectedTab === "public-notes" && (
                  <NotePage 
                    id={id || ""} 
                    isInteractionAttendee={isInteractionAttendee}
                  />
                )}

                {selectedTab === "private-notes" && (
                  <Notes
                    id={id || ""}
                    noteType="public"
                    isInteractionAttendee={isInteractionAttendee}
                    hasMeetingTimePassed={hasMeetingTimePassed}
                  />
                )}
                {
                  selectedTab === "ask-7g" && (
                    <InteractionAsk7G  interactionId={id} interactionData = {meetingData}  suggestions={[
                      "What are the key topics discussed in this meeting?",
                      "Summarize the main takeaways.",
                      "List all the action items mentioned."
                    ]} variant= "interactionPage"/>
                  )
                }
                {["BANT-analysis", "MEDDICC-analysis"].includes(
                  selectedTab
                ) && (
                  <FrameworkAnalysis
                    interactionId={id}
                    frameworkType={frameworkMode as "BANT" | "MEDDICC"}
                    interactionAssetId={condensedTranscriptAssetId}
                    groupId={selectedRecording}
                  />
                )}
          </Box>
        </Box>

        {/* GLOBAL DIALOGS */}
        <Dialog
          open={openDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            className: "join-meeting-dialog",
          }}
        >
          <JoinMeeting
            setOpen={handleCloseDialog}
            meetingId={meetingData?.interactionId}
            defaultMeetingUrl={meetingData?.joinLink}
            setIsButtonDisabled={setIsButtonDisabled}
            setTooltipMessage={setTooltipMessage}
            isUpcoming={joinMeetingMode === "upcoming"}
          />
        </Dialog>

        <ShareInteractionDialog
          open={isShareOpen}
          onClose={() => setIsShareOpen(false)}
          interactionId={id || ""}
          interactionTitle={meetingData?.title || ""}
        />

        <CustomConfirmation
          isOpen={hardDeleteCustomConfirm}
          title={"Do you want to delete the selected recording?"}
          message="The recording can not be recovered once deleted."
          handleSuccess={() => {
            if (isHardDeleting) return;
            if (id && selectedRecording) {
              deleteAssetByGroup({
                interactionId: id,
                groupId: selectedRecording,
              });
            }
          }}
          handleClose={() => setHardDeleteCustomConfrim(false)}
          width="auto"
          isLoading={isHardDeleting}
        />
      </Box>
    </>
  );
};

export default MeetingDetailsPage;
